import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { STORAGE_KEYS } from '../../modules/auth/constants';
import { Search, ArrowLeft, Edit3, X, Calendar, User, FileText, RotateCcw } from 'lucide-react';

const API_BASE = '/api/v2/category-correction-analytics';

export default function CorrectedTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedCorrection, setSelectedCorrection] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });

  const loadTickets = async (searchTerm = '', offset = 0) => {
    try {
      setLoading(true);
      setError(null);
      const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      params.append('limit', '50');
      params.append('offset', offset.toString());

      const response = await axios.get(`${API_BASE}/corrected-tickets?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setTickets(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError(response.data.message || 'Failed to load corrected tickets');
      }
    } catch (err) {
      console.error('Load corrected tickets error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load corrected tickets');
    } finally {
      setLoading(false);
    }
  };

  const loadCorrectionDetails = async (correctionId) => {
    try {
      const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
      const response = await axios.get(`${API_BASE}/correction/${correctionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setSelectedCorrection(response.data.data);
        setShowDetailsModal(true);
      } else {
        setError(response.data.message || 'Failed to load correction details');
      }
    } catch (err) {
      console.error('Load correction details error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load correction details');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadTickets(search, 0);
  };

  const handlePageChange = (newOffset) => {
    loadTickets(search, newOffset);
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif", padding: 24, background: '#F1F5F9', minHeight: '100vh' }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => navigate('/hcms/tickets')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', color: '#64748B', fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: 0 }}
            >
              <ArrowLeft size={16} />
              Back to Tickets
            </button>
            <Edit3 size={28} color="#0F172A" />
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1E293B', margin: 0 }}>Corrected Tickets</h1>
          </div>
        </div>

        <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 240, position: 'relative' }}>
              <Search size={18} color="#94A3B8" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 1 }} />
              <input
                type="text"
                placeholder="Search by ticket ID, subject, or employee name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 36px 10px 40px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 14, color: '#1E293B', outline: 'none', background: '#FFFFFF' }}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => { setSearch(''); loadTickets('', 0); }}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#94A3B8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <button
              type="submit"
              style={{ padding: '10px 20px', background: '#0F172A', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#FFFFFF', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Search
            </button>
            <button
              type="button"
              onClick={() => loadTickets(search, 0)}
              style={{ padding: '10px 14px', background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#0F172A', cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <RotateCcw size={16} />
              Refresh
            </button>
          </form>
        </div>

        {error && (
          <div style={{ padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, color: '#EF4444', fontSize: 13, marginBottom: 24 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#64748B' }}>Loading corrected tickets...</div>
        ) : tickets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#64748B', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1E293B' }}>
              {search ? `No results found for "${search}"` : 'No corrected tickets found'}
            </div>
            <div style={{ fontSize: 13 }}>
              {search ? 'Try a different search term or clear the search.' : 'Category corrections will appear here once they are made.'}
            </div>
          </div>
        ) : (
          <>
            <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #E2E8F0', background: '#F8FAFC' }}>
                    <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Ticket ID</th>
                    <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Subject</th>
                    <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Employee Name</th>
                    <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Previous Category</th>
                    <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Updated Category</th>
                    <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Corrected By</th>
                    <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Corrected Date</th>
                    <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <tr key={ticket.id} style={{ borderBottom: '1px solid #F1F5F9', cursor: 'pointer' }} onClick={() => loadCorrectionDetails(ticket.id)}>
                      <td style={{ padding: 12, color: '#1E293B', fontWeight: 600 }}>{ticket.ticket_code || ticket.id}</td>
                      <td style={{ padding: 12, color: '#1E293B', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ticket.title || '—'}</td>
                      <td style={{ padding: 12, color: '#1E293B' }}>{ticket.employee_name || '—'}</td>
                      <td style={{ padding: 12, color: '#1E293B' }}>
                        {ticket.original_category || '—'}
                        {ticket.original_subcategory && ` > ${ticket.original_subcategory}`}
                      </td>
                      <td style={{ padding: 12, color: '#1E293B' }}>
                        {ticket.corrected_category || '—'}
                        {ticket.corrected_subcategory && ` > ${ticket.corrected_subcategory}`}
                      </td>
                      <td style={{ padding: 12, color: '#1E293B' }}>{ticket.corrected_by_name || '—'}</td>
                      <td style={{ padding: 12, color: '#1E293B' }}>{formatDate(ticket.corrected_at)}</td>
                      <td style={{ padding: 12 }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); loadCorrectionDetails(ticket.id); }}
                          style={{ padding: '6px 12px', background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#0F172A', cursor: 'pointer' }}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.total > pagination.limit && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
                <button
                  onClick={() => handlePageChange(Math.max(0, pagination.offset - pagination.limit))}
                  disabled={pagination.offset === 0}
                  style={{ padding: '8px 16px', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#0F172A', cursor: pagination.offset === 0 ? 'not-allowed' : 'pointer', opacity: pagination.offset === 0 ? 0.5 : 1 }}
                >
                  Previous
                </button>
                <span style={{ padding: '8px 16px', color: '#64748B', fontSize: 13 }}>
                  {Math.floor(pagination.offset / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.offset + pagination.limit)}
                  disabled={pagination.offset + pagination.limit >= pagination.total}
                  style={{ padding: '8px 16px', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#0F172A', cursor: pagination.offset + pagination.limit >= pagination.total ? 'not-allowed' : 'pointer', opacity: pagination.offset + pagination.limit >= pagination.total ? 0.5 : 1 }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {showDetailsModal && selectedCorrection && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#FFFFFF', borderRadius: 16, width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1E293B', margin: 0 }}>Category Correction Details</h2>
                <button onClick={() => setShowDetailsModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, color: '#94A3B8' }}>
                  <X size={18} />
                </button>
              </div>
              <div style={{ padding: 24 }}>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <FileText size={18} color="#64748B" />
                    <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1E293B', margin: 0 }}>Ticket Information</h3>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Ticket ID</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1E293B' }}>{selectedCorrection.ticket_code || selectedCorrection.case_id}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Employee</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1E293B' }}>{selectedCorrection.employee_name || '—'}</div>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <Edit3 size={18} color="#64748B" />
                    <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1E293B', margin: 0 }}>Category Changes</h3>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Previous Category</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1E293B' }}>
                        {selectedCorrection.original_category || '—'}
                        {selectedCorrection.original_subcategory && ` > ${selectedCorrection.original_subcategory}`}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Updated Category</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1E293B' }}>
                        {selectedCorrection.corrected_category || '—'}
                        {selectedCorrection.corrected_subcategory && ` > ${selectedCorrection.corrected_subcategory}`}
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Correction Reason</div>
                  <div style={{ fontSize: 14, color: '#1E293B', lineHeight: 1.5, background: '#F8FAFC', padding: 12, borderRadius: 8 }}>
                    {selectedCorrection.correction_reason || '—'}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <User size={14} color="#64748B" />
                      <div style={{ fontSize: 12, color: '#64748B' }}>Corrected By</div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1E293B' }}>{selectedCorrection.corrected_by_name || '—'}</div>
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <Calendar size={14} color="#64748B" />
                      <div style={{ fontSize: 12, color: '#64748B' }}>Corrected At</div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1E293B' }}>{formatDate(selectedCorrection.corrected_at)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
